import { centsToDollars, parsePrice } from "helpers/price";

export interface IPaymentOptionMeta {
  icon: string;
  description: string;
}

export const PAYMENT_OPTION_META: Record<string, IPaymentOptionMeta> = {
  paymentPlan: {
    icon: "pi pi-list",
    description: "Set the total count and calculate each installment.",
  },
  setAmount: {
    icon: "pi pi-dollar",
    description: "Set a recurring amount and calculate the payment count.",
  },
  downPaymentOnly: {
    icon: "pi pi-wallet",
    description: "Collect a single upfront payment with no installments.",
  },
};

export interface IPlanPreviewInput {
  downPaymentInCents: number;
  installmentCount: number;
  installmentAmountInCents: number;
  /** "monthly" | "weekly" | "biweekly" — already lowercased. */
  frequencyLabel: string;
  totalPaymentCount: number;
  distributionLabel?: string | null;
  lastPaymentDate?: string | null;
  /** Only set when the final installment differs from the regular one. */
  finalPaymentInCents?: number | null;
  totalCostInCents: number;
}

const money = (cents: number) => parsePrice(centsToDollars(cents));

/**
 * The headline sentence: what the patient pays today, then the recurring
 * installments, how many payments in total and how they are distributed.
 */
export function buildPlanPreviewSummary({
  downPaymentInCents,
  installmentCount,
  installmentAmountInCents,
  frequencyLabel,
  totalPaymentCount,
  distributionLabel,
}: IPlanPreviewInput): string {
  const installments =
    installmentCount === 1
      ? `1 ${frequencyLabel} payment of approximately ${money(
          installmentAmountInCents
        )}`
      : `${installmentCount} ${frequencyLabel} payments of approximately ${money(
          installmentAmountInCents
        )}`;

  const total = distributionLabel
    ? `${totalPaymentCount} payments total, distributed ${distributionLabel.toLowerCase()}.`
    : `${totalPaymentCount} payments total.`;

  return `${money(downPaymentInCents)} down, then ${installments}. ${total}`;
}

/**
 * The follow-up sentence carrying the details the old breakdown showed as
 * separate rows: an uneven final payment, when the plan ends, and the total.
 */
export function buildPlanPreviewDetail({
  lastPaymentDate,
  finalPaymentInCents,
  totalCostInCents,
}: IPlanPreviewInput): string {
  const parts: string[] = [];

  if (finalPaymentInCents) {
    parts.push(`Final payment ${money(finalPaymentInCents)}.`);
  }
  if (lastPaymentDate) {
    parts.push(`Last payment on ${lastPaymentDate}.`);
  }
  parts.push(`Total ${money(totalCostInCents)}, no interest.`);

  return parts.join(" ");
}

/**
 * Re-validate a field only when it holds a value. A field the user has not filled in yet —
 * or one we just cleared while switching payment options — has its error removed instead,
 * so the section does not open with red errors on fields nobody has touched.
 */
export const buildRevalidateIfFilled =
  ({
    getValues,
    trigger,
    clearErrors,
  }: {
    getValues: (field: string) => any;
    trigger: (field: string) => void;
    clearErrors: (field: string) => void;
  }) =>
  (field: string) => {
    const value = getValues(field);

    if (value === null || value === undefined || value === "") {
      clearErrors(field);
      return;
    }

    trigger(field);
  };
